<template>
  <div class="home-view home-view--map">
    <HomeMapView
      :branches="filteredBranches"
      :groups="groups"
      :strict-mode="strictMode"
      @update:strict-mode="onStrictModeChange"
      @toggle-category="handleCategorySelected"
      @clear="clearFilters"
    />
  </div>
</template>

<script>
// Explicit name — required for KeepAlive :include on Vue 3.2
export default { name: "HomeView" };
</script>

<script setup>
import { ref, onMounted, watch } from "vue";
import axios from "axios";
import { api } from "@/services/api";

import HomeMapView from "@/components/home/HomeMapView.vue";

const apiUrl = process.env.VUE_APP_API_URL;

// ── State ─────────────────────────────────────
const branches         = ref([]);
const filteredBranches = ref([]);
const selectedCategory = ref([]);
const groups           = ref([]);
const strictMode       = ref(true);

// ── Data ──────────────────────────────────────
async function loadGroups() {
  try { groups.value = (await api.get("/category-groups")).data || []; } catch {}
}

async function loadBranches(page = 1) {
  const params = { page };
  if (selectedCategory.value.length) {
    params["category_ids[]"] = selectedCategory.value;
    params.strict = strictMode.value ? 1 : 0;
  }
  const { data: result } = await axios.get(`${apiUrl}/branches`, { params });
  const items = result.data ?? result;

  branches.value = page === 1
    ? items
    : [...branches.value, ...items.filter(b => !new Set(branches.value.map(x => x.id)).has(b.id))];

  filteredBranches.value = branches.value;

  // Always silently load all remaining pages — map needs them
  if (result.next_page_url) {
    loadAllBranchesForMap(page + 1);
  }
}

async function loadAllBranchesForMap(startPage = 2) {
  let page = startPage;
  const maxPages = 20;
  while (page <= maxPages) {
    try {
      const params = { page };
      if (selectedCategory.value.length) {
        params["category_ids[]"] = selectedCategory.value;
        params.strict = strictMode.value ? 1 : 0;
      }
      const { data: result } = await axios.get(`${apiUrl}/branches`, { params });
      const items = result.data ?? result;
      if (!items.length) break;

      const existing = new Set(branches.value.map(x => x.id));
      const newItems = items.filter(b => !existing.has(b.id));
      if (newItems.length) {
        branches.value         = [...branches.value, ...newItems];
        filteredBranches.value = branches.value;
      }

      if (!result.next_page_url) break;
      page++;
    } catch { break; }
  }
}

// ── Filters ───────────────────────────────────
async function handleCategorySelected(id) {
  selectedCategory.value = selectedCategory.value.includes(id)
    ? selectedCategory.value.filter(x => x !== id)
    : [...selectedCategory.value, id];
  await loadBranches(1);
  logFilterJourney();
}

async function onStrictModeChange(val) {
  strictMode.value = val;
  if (selectedCategory.value.length) await loadBranches(1);
}

function clearFilters() {
  selectedCategory.value = [];
  strictMode.value       = true;
  loadBranches(1);
}

async function logFilterJourney() {
  if (!localStorage.getItem("token") || !selectedCategory.value.length) return;
  try {
    await api.post("/user-events", {
      event_type: "category_filter_applied",
      meta: {
        selected_category_ids: [...selectedCategory.value],
        strict_mode: strictMode.value,
        results_count: filteredBranches.value.length,
      },
    });
  } catch {}
}

watch(strictMode, () => { if (selectedCategory.value.length) loadBranches(1); });

onMounted(() => Promise.all([loadBranches(1), loadGroups()]));
</script>

<style scoped lang="scss">
.home-view {
  background: var(--color-bg);
  min-height: 100vh;
  direction: rtl;

  &--map {
    position: relative;
    overflow: hidden;
  }
}
</style>